import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { L as Link } from "../lib/i18n";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { api } from "../lib/api";
import { Mail, CheckCircle2, AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const { t } = useLang();
  const { applyAuthResponse } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const tokenMissing = !token || token.length < 20;

  // status: idle | loading | success | error
  const [status, setStatus] = useState(tokenMissing ? "missing" : "loading");
  const [newEmail, setNewEmail] = useState("");
  const [error, setError] = useState("");
  const ranRef = useRef(false);

  useEffect(() => {
    if (tokenMissing) return;
    if (ranRef.current) return; // StrictMode-safe single fire
    ranRef.current = true;
    (async () => {
      try {
        const res = await api.verifyEmailChange(token);
        if (applyAuthResponse) applyAuthResponse(res);
        setNewEmail(res.email || "");
        setStatus("success");
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || "Error";
        setError(detail);
        setStatus("error");
      }
    })();
  }, [token, tokenMissing, applyAuthResponse]);

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-4">
      <Card className="w-full border-zinc-800 bg-zinc-950 p-8 text-white" data-testid="verify-email-card">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">CarHive</p>
        <h1 className="mt-2 font-display text-3xl font-bold">{t.auth.verifyEmailTitle}</h1>

        {status === "loading" && (
          <div className="mt-8 flex items-center gap-3 rounded-lg border border-zinc-800 bg-black/40 p-4" data-testid="verify-email-loading">
            <Loader2 className="h-5 w-5 animate-spin text-red-500" />
            <p className="text-sm text-zinc-300">{t.auth.verifyEmailLoading}</p>
          </div>
        )}

        {status === "missing" && (
          <div className="mt-6 space-y-5" data-testid="verify-email-missing">
            <div className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
              <p className="text-sm leading-relaxed text-zinc-200">{t.auth.verifyEmailMissingToken}</p>
            </div>
            <Button asChild className="bg-red-600 text-white hover:bg-red-500">
              <Link to="/account" data-testid="verify-email-go-account">{t.auth.verifyEmailGoAccount}</Link>
            </Button>
          </div>
        )}

        {status === "success" && (
          <div className="mt-6 space-y-5" data-testid="verify-email-success">
            <div className="flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
              <div className="text-sm leading-relaxed text-zinc-200">
                <p>{t.auth.verifyEmailSuccess}</p>
                {newEmail && (
                  <p className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-black px-2.5 py-1.5 text-xs font-mono text-emerald-300">
                    <Mail className="h-3 w-3" /> {newEmail}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button asChild className="bg-red-600 text-white hover:bg-red-500" data-testid="verify-email-go-dashboard">
                <Link to="/dashboard">{t.auth.verifyEmailGoDashboard}</Link>
              </Button>
              <Button asChild variant="outline" className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
                <Link to="/account" data-testid="verify-email-go-account">{t.auth.verifyEmailGoAccount}</Link>
              </Button>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="mt-6 space-y-5" data-testid="verify-email-error">
            <div className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
              <div className="text-sm leading-relaxed text-zinc-200">
                <p className="font-semibold">{t.auth.verifyEmailError}</p>
                <p className="mt-1 text-zinc-400">{error}</p>
              </div>
            </div>
            <Button asChild className="bg-red-600 text-white hover:bg-red-500">
              <Link to="/account">{t.auth.verifyEmailGoAccount}</Link>
            </Button>
          </div>
        )}

        <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-red-500" data-testid="verify-email-back-login">
          <ArrowLeft className="h-3.5 w-3.5" /> {t.auth.forgotBack}
        </Link>
      </Card>
    </div>
  );
}
